// NOTE: This file is stable and usually should not be modified.
// It is important that all functionality in this file is preserved, and should only be modified if explicitly requested.

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Upload, AlertTriangle, ChevronDown, ChevronUp, Loader2, Copy, Check, ExternalLink, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import {
  useLoginActions,
  generateNostrConnectParams,
  generateNostrConnectURI,
  type NostrConnectParams,
} from '@/hooks/useLoginActions';
import { DialogTitle } from '@radix-ui/react-dialog';
import { useIsMobile } from '@/hooks/useIsMobile';
import SignupDialog from './SignupDialog';

interface LoginDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
}

const validateNsec = (nsec: string) => {
  return /^nsec1[a-zA-Z0-9]{58}$/.test(nsec);
};

const validateBunkerUri = (uri: string) => {
  return uri.startsWith('bunker://');
};

/** Check if running on actual mobile device (not just small screen) */
function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

const LoginDialog: React.FC<LoginDialogProps> = ({ isOpen, onClose, onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [nsec, setNsec] = useState('');
  const [bunkerUri, setBunkerUri] = useState('');
  const [nostrConnectParams, setNostrConnectParams] = useState<NostrConnectParams | null>(null);
  const [nostrConnectUri, setNostrConnectUri] = useState<string>('');
  const [isWaitingForConnect, setIsWaitingForConnect] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showBunkerInput, setShowBunkerInput] = useState(false);
  const [errors, setErrors] = useState<{
    nsec?: string;
    bunker?: string;
    file?: string;
    extension?: string;
  }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const login = useLoginActions();

  // Check if on mobile device
  const isMobile = useIsMobile();
  // Check if extension is available
  const hasExtension = 'nostr' in window;

  // Generate nostrconnect params (sync) - just creates the QR code data
  const generateConnectSession = useCallback(() => {
    const relayUrls = login.getRelayUrls();
    const params = generateNostrConnectParams(relayUrls);
    const uri = generateNostrConnectURI(params, {
      callback: isMobileDevice() ? `${window.location.origin}/remoteloginsuccess` : undefined,
    });
    setNostrConnectParams(params);
    setNostrConnectUri(uri);
    setConnectError(null);
  }, [login]);

  // Start listening for connection (async) - runs after params are set
  useEffect(() => {
    if (!nostrConnectParams || isWaitingForConnect) return;

    const startListening = async () => {
      setIsWaitingForConnect(true);
      abortControllerRef.current = new AbortController();

      try {
        await login.nostrconnect(nostrConnectParams, abortControllerRef.current.signal);
        onLogin();
        onClose();
      } catch (error) {
        console.error('Nostrconnect failed:', error);
        // Don't show error if it was aborted (dialog closed)
        if (error instanceof Error && error.name !== 'AbortError') {
          setConnectError(error.message);
        }
        setIsWaitingForConnect(false);
      }
    };

    startListening();
  }, [nostrConnectParams, login, onLogin, onClose, isWaitingForConnect]);

  // Clean up on close
  useEffect(() => {
    if (!isOpen) {
      setNostrConnectParams(null);
      setNostrConnectUri('');
      setIsWaitingForConnect(false);
      setConnectError(null);
      setNsec('');
      setBunkerUri('');
      setErrors({});
      setIsLoading(false);
      setIsFileLoading(false);
      setShowBunkerInput(false);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  // Retry connection with new params
  const handleRetry = useCallback(() => {
    setNostrConnectParams(null);
    setNostrConnectUri('');
    setIsWaitingForConnect(false);
    setConnectError(null);
    // Generate new session after state clears
    setTimeout(() => generateConnectSession(), 0);
  }, [generateConnectSession]);

  const handleCopyUri = async () => {
    await navigator.clipboard.writeText(nostrConnectUri);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Open the nostrconnect URI in the system - this will launch a signer app like Amber if installed
  const handleOpenSignerApp = () => {
    if (!nostrConnectUri) return;
    window.location.href = nostrConnectUri;
  };

  const handleExtensionLogin = async () => {
    setIsLoading(true);
    setErrors(prev => ({ ...prev, extension: undefined }));

    try {
      if (!('nostr' in window)) {
        throw new Error('Nostr extension not found. Please install a NIP-07 extension.');
      }
      await login.extension();
      onLogin();
      onClose();
    } catch (e: unknown) {
      const error = e as Error;
      console.error('Extension login failed:', error);
      setErrors(prev => ({
        ...prev,
        extension: error instanceof Error ? error.message : 'Extension login failed'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const executeLogin = (key: string) => {
    setIsLoading(true);
    setErrors({});

    // Use a timeout to allow the UI to update before the synchronous login call
    setTimeout(() => {
      try {
        login.nsec(key);
        onLogin();
        onClose();
      } catch {
        setErrors({ nsec: "Failed to login with this key. Please check that it's correct." });
        setIsLoading(false);
      }
    }, 50);
  };

  const handleKeyLogin = () => {
    if (!nsec.trim()) {
      setErrors(prev => ({ ...prev, nsec: 'Please enter your secret key' }));
      return;
    }

    if (!validateNsec(nsec)) {
      setErrors(prev => ({ ...prev, nsec: 'Invalid secret key format. Must be a valid nsec starting with nsec1.' }));
      return;
    }
    executeLogin(nsec);
  };

  const handleBunkerLogin = async () => {
    if (!bunkerUri.trim()) {
      setErrors(prev => ({ ...prev, bunker: 'Please enter a bunker URI' }));
      return;
    }

    if (!validateBunkerUri(bunkerUri)) {
      setErrors(prev => ({ ...prev, bunker: 'Invalid bunker URI format. Must start with bunker://' }));
      return;
    }

    setIsLoading(true);
    setErrors(prev => ({ ...prev, bunker: undefined }));

    try {
      await login.bunker(bunkerUri);
      onLogin();
      onClose();
      // Clear the URI from memory
      setBunkerUri('');
    } catch {
      setErrors(prev => ({
        ...prev,
        bunker: 'Failed to connect to bunker. Please check the URI.'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsFileLoading(true);
    setErrors({});

    const reader = new FileReader();
    reader.onload = (event) => {
      setIsFileLoading(false);
      const content = event.target?.result as string;
      if (content) {
        const trimmedContent = content.trim();
        if (validateNsec(trimmedContent)) {
          executeLogin(trimmedContent);
        } else {
          setErrors({ file: 'File does not contain a valid secret key.' });
        }
      } else {
        setErrors({ file: 'Could not read file content.' });
      }
    };
    reader.onerror = () => {
      setIsFileLoading(false);
      setErrors({ file: 'Failed to read file.' });
    };
    reader.readAsText(file);
  };

  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);

  const renderTabs = () => (
    <Tabs
      defaultValue="key"
      className="w-full"
      onValueChange={(value) => {
        if (value === 'remote' && !nostrConnectParams && !connectError) {
          generateConnectSession();
        }
      }}
    >
      <TabsList className="grid w-full grid-cols-2 bg-cream-dark/60 rounded-none mb-4 h-auto p-0">
        <TabsTrigger value="key" className="flex items-center gap-2 rounded-none font-hand text-base py-2.5 data-[state=active]:bg-polaroid-white data-[state=active]:shadow-[0_2px_6px_rgba(45,27,78,0.1)]">
          <span>Secret Key</span>
        </TabsTrigger>
        <TabsTrigger value="remote" className="flex items-center gap-2 rounded-none font-hand text-base py-2.5 data-[state=active]:bg-polaroid-white data-[state=active]:shadow-[0_2px_6px_rgba(45,27,78,0.1)]">
          <span>Remote Signer</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value='key' className='space-y-4'>
        <form onSubmit={(e) => {
          e.preventDefault();
          handleKeyLogin();
        }} className='space-y-4'>
          <div className='space-y-2'>
            <Input
              id='nsec'
              type="password"
              value={nsec}
              onChange={(e) => {
                setNsec(e.target.value);
                if (errors.nsec) setErrors(prev => ({ ...prev, nsec: undefined }));
              }}
              className={`rounded-none bg-polaroid-white border-cream-dark font-mono text-sm ${
                errors.nsec ? 'border-red-500 focus-visible:ring-red-500' : ''
              }`}
              placeholder='nsec1...'
              autoComplete="off"
            />
            {errors.nsec && (
              <p className="font-hand text-sm text-red-500">{errors.nsec}</p>
            )}
          </div>

          <div className="flex space-x-2">
            <button
              type="submit"
              disabled={isLoading || !nsec.trim()}
              className="flex-1 bg-polaroid-white py-3 px-6 shadow-[0_2px_8px_rgba(45,27,78,0.1)] transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-[0_4px_14px_rgba(45,27,78,0.14)] font-hand text-lg font-bold text-pol-text disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isLoading ? 'Verifying...' : 'Log in'}
            </button>

            <input
              type="file"
              accept=".txt"
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileUpload}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || isFileLoading}
              className="bg-polaroid-white py-3 px-4 shadow-[0_2px_8px_rgba(45,27,78,0.1)] transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-[0_4px_14px_rgba(45,27,78,0.14)] text-pol-text disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
            </button>
          </div>

          {errors.file && (
            <p className="font-hand text-sm text-red-500 text-center">{errors.file}</p>
          )}
        </form>
      </TabsContent>

      <TabsContent value='remote' className='space-y-4'>
        {/* Nostrconnect Section */}
        <div className='flex flex-col items-center space-y-4'>
          {connectError ? (
            <div className='flex flex-col items-center space-y-4 py-4'>
              <p className='font-hand text-sm text-red-500 text-center'>{connectError}</p>
              <button
                onClick={handleRetry}
                className="bg-polaroid-white py-2 px-5 shadow-[0_2px_8px_rgba(45,27,78,0.1)] transition-all active:scale-[0.97] hover:-translate-y-0.5 font-hand text-base text-pol-text"
              >
                Retry
              </button>
            </div>
          ) : nostrConnectUri ? (
            <>
              {/* QR Code - only show on desktop */}
              {!isMobile && (
                <div className='p-4 bg-polaroid-white shadow-[0_2px_8px_rgba(45,27,78,0.1)]'>
                  <QRCodeCanvas
                    value={nostrConnectUri}
                    size={180}
                    level='M'
                  />
                </div>
              )}

              {/* Status message */}
              <div className='flex items-center gap-2 font-hand text-sm text-pol-text-secondary'>
                <span>{isMobile ? 'Tap to open your signer app' : 'Scan with your signer app'}</span>
              </div>

              {/* Open Signer App button - primary action on mobile */}
              {isMobile && (
                <button
                  className='w-full gap-2 py-4 flex items-center justify-center bg-polaroid-white shadow-[0_2px_8px_rgba(45,27,78,0.1)] transition-all active:scale-[0.97] hover:-translate-y-0.5 font-hand text-lg font-bold text-pol-text'
                  onClick={handleOpenSignerApp}
                >
                  <ExternalLink className='w-5 h-5' />
                  Open Signer App
                </button>
              )}

              {/* Copy button */}
              <button
                className='gap-2 py-2 px-5 flex items-center justify-center bg-polaroid-white shadow-[0_2px_6px_rgba(45,27,78,0.08)] transition-all active:scale-[0.97] hover:-translate-y-0.5 font-hand text-sm text-pol-text-secondary hover:text-pol-text'
                onClick={handleCopyUri}
              >
                {copied ? (
                  <>
                    <Check className='w-4 h-4' />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className='w-4 h-4' />
                    Copy URI
                  </>
                )}
              </button>
            </>
          ) : (
            <div className='flex items-center justify-center h-[100px]'>
              <Loader2 className='w-8 h-8 animate-spin text-pol-text-secondary' />
            </div>
          )}
        </div>

        {/* Manual URI input section - collapsible */}
        <div className='pt-4 border-t-2 border-dotted border-pol-text/10'>
          <button
            type='button'
            onClick={() => setShowBunkerInput(!showBunkerInput)}
            className='flex items-center justify-center gap-2 w-full font-hand text-sm text-pol-text-secondary hover:text-pol-text transition-colors py-2'
          >
            <span>Enter bunker URI manually</span>
            {showBunkerInput ? (
              <ChevronUp className='w-4 h-4' />
            ) : (
              <ChevronDown className='w-4 h-4' />
            )}
          </button>

          {showBunkerInput && (
            <div className='space-y-3 mt-3'>
              <div className='space-y-2'>
                <Input
                  id='connectBunkerUri'
                  value={bunkerUri}
                  onChange={(e) => setBunkerUri(e.target.value)}
                  className='rounded-none bg-polaroid-white border-cream-dark font-mono text-sm'
                  placeholder='bunker://'
                />
                {bunkerUri && !validateBunkerUri(bunkerUri) && (
                  <p className='text-red-500 font-hand text-xs'>Invalid bunker URI format</p>
                )}
              </div>

              <button
                className='w-full py-3 bg-polaroid-white shadow-[0_2px_8px_rgba(45,27,78,0.1)] transition-all active:scale-[0.97] hover:-translate-y-0.5 font-hand text-base text-pol-text disabled:opacity-40 disabled:cursor-not-allowed'
                onClick={handleBunkerLogin}
                disabled={isLoading || !bunkerUri.trim() || !validateBunkerUri(bunkerUri)}
              >
                {isLoading ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );

  return (
    <>
    <SignupDialog isOpen={signupOpen} onClose={() => setSignupOpen(false)} />
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-sm max-h-[90dvh] p-0 gap-0 overflow-hidden rounded-none overflow-y-auto bg-cream border-0 shadow-[0_8px_40px_rgba(45,27,78,0.25)]">
        {/* Decorative tape at top */}
        <div className="relative flex justify-center pt-2">
          <div
            className="w-[100px] h-[20px] tape-rainbow"
            style={{ transform: 'rotate(-2deg)' }}
          />
        </div>

        <DialogHeader className="px-6 pt-3 pb-0">
          <DialogTitle className="font-hand text-2xl font-bold text-pol-text text-center">
            Log in
          </DialogTitle>
        </DialogHeader>

        {/* Key icon as a mini polaroid */}
        <div className="flex justify-center py-3">
          <div className="bg-polaroid-white p-3 pb-5 shadow-[0_2px_10px_rgba(45,27,78,0.12)]" style={{ transform: 'rotate(-2deg)' }}>
            <div className="w-24 h-20 bg-cream-dark flex items-center justify-center">
              <span className="text-5xl">🔑</span>
            </div>
          </div>
        </div>

        <div className='px-6 pb-6 space-y-4 overflow-y-auto'>
          {/* Create new account */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                onClose();
                setTimeout(() => setSignupOpen(true), 200);
              }}
              className="inline-flex items-center gap-1.5 font-hand text-sm text-pol-text-secondary hover:text-pol-text transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              <span>Create a new account</span>
            </button>
          </div>

          <div className="border-t-2 border-dotted border-pol-text/10" />

          {/* Extension Login Button - shown if extension is available */}
          {hasExtension && (
            <div className="space-y-3">
              {errors.extension && (
                <Alert variant="destructive" className="rounded-none">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="font-hand">{errors.extension}</AlertDescription>
                </Alert>
              )}
              <button
                className="relative w-full bg-polaroid-white py-3.5 px-6 shadow-[0_2px_8px_rgba(45,27,78,0.1)] transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-[0_4px_14px_rgba(45,27,78,0.14)] disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={handleExtensionLogin}
                disabled={isLoading}
                style={{ transform: 'rotate(0.5deg)' }}
              >
                {/* Mini tape strip */}
                <div
                  className="absolute top-0 left-1/2 w-[50px] h-[12px] tape-rainbow"
                  style={{ transform: 'translateX(-50%) translateY(-6px) rotate(-1deg)' }}
                />
                <span className="font-hand text-lg font-bold text-pol-text">
                  {isLoading ? 'Logging in...' : 'Log in with Extension'}
                </span>
              </button>
            </div>
          )}

          {/* Tabs - wrapped in collapsible if extension is available, otherwise shown directly */}
          {hasExtension ? (
            <Collapsible className="space-y-4" open={isMoreOptionsOpen} onOpenChange={setIsMoreOptionsOpen}>
              <button
                type="button"
                onClick={() => setIsMoreOptionsOpen(!isMoreOptionsOpen)}
                className="w-full flex items-center justify-center gap-1 font-hand text-sm text-pol-text-secondary hover:text-pol-text transition-colors py-2"
              >
                <span>More Options</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMoreOptionsOpen ? 'rotate-180' : ''}`} />
              </button>

              <CollapsibleContent>
                {renderTabs()}
              </CollapsibleContent>
            </Collapsible>
          ) : (
            renderTabs()
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
    );
  };

export default LoginDialog;
